<template>
  <div class="leaderboard">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>🏆 龙虎榜 — 业绩排行</h2>
      <p>展示平台优秀会员与团队业绩排名</p>
    </div>

    <!-- 统计卡片 -->
    <el-row :gutter="24" class="stats-row" v-loading="loading">
      <el-col :span="6">
        <el-card shadow="never" class="stat-card">
          <div class="stat-icon blue"><i class="el-icon-star-on" /></div>
          <div class="stat-info">
            <p class="stat-label">{{ period === 'daily' ? '今日' : '本月' }}榜首业绩</p>
            <h3 class="stat-value">¥{{ formatAmount(stats.topPersonal ? stats.topPersonal.performance : 0) }}</h3>
            <p class="stat-top-name blue">榜首：{{ stats.topPersonal ? stats.topPersonal.name : '-' }}</p>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="never" class="stat-card">
          <div class="stat-icon red"><i class="el-icon-medal" /></div>
          <div class="stat-info">
            <p class="stat-label">历史最高个人业绩</p>
            <h3 class="stat-value">-</h3>
            <p class="stat-top-name red">敬请期待</p>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="never" class="stat-card">
          <div class="stat-icon orange"><i class="el-icon-user-solid" /></div>
          <div class="stat-info">
            <p class="stat-label">{{ period === 'daily' ? '今日' : '本月' }}团队榜首</p>
            <h3 class="stat-value">¥{{ formatAmount(stats.topTeam ? stats.topTeam.performance : 0) }}</h3>
            <p class="stat-top-name orange">榜首：{{ stats.topTeam ? stats.topTeam.name : '-' }}</p>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="never" class="stat-card">
          <div class="stat-icon green"><i class="el-icon-data-line" /></div>
          <div class="stat-info">
            <p class="stat-label">历史最高团队业绩</p>
            <h3 class="stat-value">-</h3>
            <p class="stat-top-name green">敬请期待</p>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 榜单内容 -->
    <el-card shadow="never" class="board-card">
      <!-- 维度切换 -->
      <el-tabs v-model="dimension" @tab-click="handleDimensionChange">
        <el-tab-pane label="👤 个人业绩明星榜" name="personal" />
        <el-tab-pane label="👥 团队业绩前十榜" name="team" />
      </el-tabs>

      <!-- 时间周期切换 -->
      <div class="period-bar">
        <div class="period-btns">
          <el-radio-group v-model="period" size="small" @change="handlePeriodChange">
            <el-radio-button label="daily">📅 每日榜单</el-radio-button>
            <el-radio-button label="monthly">📅 月度榜单</el-radio-button>
          </el-radio-group>
        </div>
        <div class="period-date">当前日期：{{ currentDate || '加载中...' }}</div>
      </div>

      <!-- 个人榜单 -->
      <div v-if="dimension === 'personal'" v-loading="loading">
        <el-empty v-if="personalData.length === 0" description="暂无排行数据" />
        <template v-else>
          <!-- Top 1 -->
          <div class="top1-card">
            <div class="top1-crown">👑</div>
            <div class="top1-avatar">
              <el-avatar :size="72" icon="el-icon-user-solid" />
            </div>
            <div class="top1-info">
              <div class="top1-name">
                <span>{{ personalData[0].name }}</span>
                <el-tag size="small" :type="personalData[0].identity_code === 2 ? 'warning' : 'success'">{{ personalData[0].identity }}</el-tag>
              </div>
              <div class="top1-stars">⭐⭐⭐⭐⭐</div>
            </div>
            <div class="top1-performance">
              <p class="top1-label">当前周期业绩</p>
              <p class="top1-amount">{{ personalData[0].performance }}</p>
            </div>
          </div>

          <!-- 2-10 名 -->
          <el-table :data="personalData.slice(1)" style="width: 100%;" border stripe>
            <el-table-column label="排名" width="80" align="center">
              <template slot-scope="scope">
                <span class="rank-badge" :class="'rank-' + (scope.$index + 2)">{{ scope.$index + 2 }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="name" label="会员名" width="150" />
            <el-table-column prop="phone" label="手机号" width="150" />
            <el-table-column label="会员身份" width="120">
              <template slot-scope="scope">
                <el-tag :type="scope.row.identity_code === 2 ? 'warning' : 'success'" size="small">{{ scope.row.identity }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="业绩" width="150">
              <template slot-scope="scope">
                <span class="price-bold">{{ scope.row.performance }}</span>
              </template>
            </el-table-column>
            <el-table-column label="所在团队" width="180">
              <template slot-scope="scope">
                <span class="branch-name">{{ scope.row.team }}</span>
              </template>
            </el-table-column>
            <el-table-column label="排名变化" width="100">
              <template slot-scope="scope">
                <span v-if="scope.row.change > 0" class="change-up"><i class="el-icon-top"></i> {{ scope.row.change }}</span>
                <span v-else-if="scope.row.change < 0" class="change-down"><i class="el-icon-bottom"></i> {{ Math.abs(scope.row.change) }}</span>
                <span v-else class="change-same">-</span>
              </template>
            </el-table-column>
          </el-table>
        </template>
      </div>

      <!-- 团队榜单 -->
      <div v-if="dimension === 'team'" v-loading="loading">
        <el-empty v-if="teamData.length === 0" description="暂无排行数据" />
        <el-table v-else :data="teamData" style="width: 100%;" border stripe>
          <el-table-column label="排名" width="80" align="center">
            <template slot-scope="scope">
              <span class="rank-badge" :class="'rank-' + (scope.$index + 1)">
                <span v-if="scope.$index === 0">👑</span>
                <span v-else>{{ scope.$index + 1 }}</span>
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="name" label="团队名称" min-width="180" />
          <el-table-column prop="leader" label="团队负责人" width="120" />
          <el-table-column label="团队业绩" width="160">
            <template slot-scope="scope">
              <span class="price-bold">{{ scope.row.performance }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="memberCount" label="团队成员数" width="120" />
          <el-table-column label="较上月" width="120">
            <template slot-scope="scope">
              <span v-if="scope.row.monthChange > 0" class="change-up"><i class="el-icon-top"></i> +{{ scope.row.monthChange }}%</span>
              <span v-else-if="scope.row.monthChange < 0" class="change-down"><i class="el-icon-bottom"></i> {{ scope.row.monthChange }}%</span>
              <span v-else class="change-same">-</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>
  </div>
</template>

<script>
import { getLeaderboard } from '@/api'

export default {
  name: 'Leaderboard',
  data() {
    return {
      dimension: 'personal',
      period: 'daily',
      loading: false,
      stats: {
        topPersonal: null,
        topTeam: null
      },
      personalData: [],
      teamData: [],
      currentDate: ''
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    handleDimensionChange() {
      this.fetchData()
    },
    handlePeriodChange() {
      this.fetchData()
    },
    fetchData() {
      this.loading = true
      getLeaderboard({ dimension: this.dimension, type: this.period }).then(res => {
        const data = res
        this.stats = data.stats || { topPersonal: null, topTeam: null }
        this.personalData = (data.personalRanking || []).map(item => ({
          ...item,
          performance: '¥' + this.formatAmount(item.performance),
          identity: item.identity || '有效会员'
        }))
        this.teamData = (data.teamRanking || []).map(item => ({
          ...item,
          performance: '¥' + this.formatAmount(item.performance),
          monthChange: item.monthChange || 0
        }))
        this.currentDate = data.currentDate || ''
      }).catch(() => {
        this.$message.error('获取龙虎榜数据失败')
      }).finally(() => {
        this.loading = false
      })
    },
    formatAmount(amount) {
      return Number(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
  }
}
</script>

<style scoped>
.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
  margin: 0 0 8px;
}

.page-header p {
  font-size: 13px;
  color: #909399;
  margin: 0;
}

.stats-row {
  margin-bottom: 24px;
}

.stat-card {
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.stat-icon.blue { background: #ecf5ff; color: #409EFF; }
.stat-icon.red { background: #fef0f0; color: #E4393C; }
.stat-icon.orange { background: #fdf6ec; color: #e6a23c; }
.stat-icon.green { background: #f0f9eb; color: #67c23a; }

.stat-info { flex: 1; min-width: 0; }
.stat-label { font-size: 13px; color: #909399; margin: 0 0 4px; }
.stat-value { font-size: 20px; font-weight: bold; margin: 0 0 4px; color: #303133; }
.stat-top-name { font-size: 12px; margin: 0; }
.stat-top-name.blue { color: #409EFF; }
.stat-top-name.red { color: #E4393C; }
.stat-top-name.orange { color: #e6a23c; }
.stat-top-name.green { color: #67c23a; }

.board-card {
  border-radius: 8px;
}

.period-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.period-date {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

.top1-card {
  background: linear-gradient(135deg, #fef9e7 0%, #fdf2d0 100%);
  border: 1px solid #f9e4a7;
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 24px;
  position: relative;
}

.top1-crown {
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 32px;
}

.top1-avatar {
  border: 4px solid #f0c040;
  border-radius: 50%;
  padding: 2px;
  background: #fff;
}

.top1-info {
  flex: 1;
}

.top1-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 22px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 4px;
}

.top1-stars {
  color: #f0c040;
  font-size: 14px;
}

.top1-performance {
  text-align: right;
}

.top1-label {
  font-size: 13px;
  color: #909399;
  margin: 0 0 4px;
}

.top1-amount {
  font-size: 36px;
  font-weight: 900;
  color: #e6a23c;
  margin: 0;
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-weight: bold;
  font-size: 14px;
}

.rank-1 { background: #fef0f0; color: #E4393C; }
.rank-2 { background: #fdf6ec; color: #e6a23c; }
.rank-3 { background: #fdf6ec; color: #e6a23c; }
.rank-4, .rank-5, .rank-6, .rank-7, .rank-8, .rank-9, .rank-10 { background: #f5f7fa; color: #909399; }

.price-bold {
  font-weight: bold;
  color: #303133;
}

.branch-name {
  color: #409EFF;
}

.change-up { color: #67c23a; font-weight: 500; }
.change-down { color: #E4393C; font-weight: 500; }
.change-same { color: #c0c4cc; }
</style>
